/**
 * Cliente de API para Compras, Proveedores y Cuentas por Pagar.
 */

import { apiRequest } from "./api";

export interface SupplierPublic {
  id: string;
  name: string;
  tax_id: string | null;
  contact_name: string | null;
  email: string | null;
  phone: string | null;
  address: string | null;
  credit_days: number;
  notes: string | null;
  is_active: boolean;
  created_at: string;
}

export interface SupplierCreate {
  name: string;
  tax_id?: string | null;
  contact_name?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  credit_days?: number;
  notes?: string | null;
}

export interface SupplierUpdate {
  name?: string | null;
  tax_id?: string | null;
  contact_name?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  credit_days?: number | null;
  notes?: string | null;
  is_active?: boolean | null;
}

export interface PurchaseOrderLinePublic {
  id: string;
  purchase_order_id: string;
  variant_id: string;
  sku: string;
  name: string;
  quantity: string;
  quantity_received: string;
  unit_cost_cents: number;
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
}

export interface PurchaseOrderPublic {
  id: string;
  branch_id: string;
  warehouse_id: string;
  supplier_id: string;
  series: string;
  folio: number;
  status: "borrador" | "confirmada" | "recibida" | "cancelada";
  issue_date: string;
  expected_delivery_date: string | null;
  subtotal_cents: number;
  tax_cents: number;
  total_cents: number;
  notes: string | null;
  created_at: string;
  received_at: string | null;
}

export interface PurchaseOrderDetailPublic {
  order: PurchaseOrderPublic;
  supplier: SupplierPublic;
  lines: PurchaseOrderLinePublic[];
}

export interface PurchaseOrderLineInput {
  variant_id: string;
  quantity: number | string;
  unit_cost_cents: number;
  tax_rate?: number | string;
}

export interface PurchaseOrderCreate {
  branch_id: string;
  warehouse_id: string;
  supplier_id: string;
  expected_delivery_date?: string | null;
  notes?: string | null;
  lines: PurchaseOrderLineInput[];
}

export interface PurchaseOrderReceiveItem {
  line_id: string;
  quantity_to_receive: number | string;
}

export interface PurchaseOrderReceiveRequest {
  invoice_number?: string | null;
  items?: PurchaseOrderReceiveItem[] | null;
  notes?: string | null;
}

export interface PayableInvoicePublic {
  id: string;
  supplier_id: string;
  purchase_order_id: string | null;
  invoice_number: string;
  status: "pendiente" | "parcial" | "pagada";
  issue_date: string;
  due_date: string;
  total_cents: number;
  paid_cents: number;
  balance_cents: number;
  notes: string | null;
  created_at: string;
}

export interface PayablePaymentPublic {
  id: string;
  payable_invoice_id: string;
  amount_cents: number;
  payment_method: string;
  reference: string | null;
  payment_date: string;
  notes: string | null;
  created_at: string;
}

export interface PayablePaymentCreate {
  amount_cents: number;
  payment_method: string;
  reference?: string | null;
  payment_date?: string | null;
  notes?: string | null;
}

const BASE = "/api/v1/purchasing";

// --- Proveedores ---
export function listSuppliers(params?: {
  q?: string;
  is_active?: boolean;
  limit?: number;
  offset?: number;
}): Promise<{ items: SupplierPublic[]; total: number }> {
  const query = new URLSearchParams();
  if (params?.q) query.set("q", params.q);
  if (params?.is_active !== undefined) query.set("is_active", String(params.is_active));
  if (params?.limit) query.set("limit", String(params.limit));
  if (params?.offset) query.set("offset", String(params.offset));
  const qs = query.toString();
  return apiRequest(`${BASE}/suppliers${qs ? `?${qs}` : ""}`);
}

export function createSupplier(payload: SupplierCreate): Promise<SupplierPublic> {
  return apiRequest(`${BASE}/suppliers`, { method: "POST", body: payload });
}

export function updateSupplier(id: string, payload: SupplierUpdate): Promise<SupplierPublic> {
  return apiRequest(`${BASE}/suppliers/${id}`, { method: "PATCH", body: payload });
}

// --- Órdenes de Compra ---
export function listPurchaseOrders(params?: {
  supplier_id?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: PurchaseOrderPublic[]; total: number }> {
  const query = new URLSearchParams();
  if (params?.supplier_id) query.set("supplier_id", params.supplier_id);
  if (params?.status) query.set("status", params.status);
  if (params?.limit) query.set("limit", String(params.limit));
  if (params?.offset) query.set("offset", String(params.offset));
  const qs = query.toString();
  return apiRequest(`${BASE}/orders${qs ? `?${qs}` : ""}`);
}

export function getPurchaseOrderDetail(id: string): Promise<PurchaseOrderDetailPublic> {
  return apiRequest(`${BASE}/orders/${id}`);
}

export function createPurchaseOrder(payload: PurchaseOrderCreate): Promise<PurchaseOrderDetailPublic> {
  return apiRequest(`${BASE}/orders`, { method: "POST", body: payload });
}

export function confirmPurchaseOrder(id: string): Promise<PurchaseOrderPublic> {
  return apiRequest(`${BASE}/orders/${id}/confirm`, { method: "POST" });
}

export function receivePurchaseOrder(
  id: string,
  payload: PurchaseOrderReceiveRequest
): Promise<PurchaseOrderDetailPublic> {
  return apiRequest(`${BASE}/orders/${id}/receive`, { method: "POST", body: payload });
}

// --- Cuentas por Pagar ---
export function listPayableInvoices(params?: {
  supplier_id?: string;
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<{ items: PayableInvoicePublic[]; total: number }> {
  const query = new URLSearchParams();
  if (params?.supplier_id) query.set("supplier_id", params.supplier_id);
  if (params?.status) query.set("status", params.status);
  if (params?.limit) query.set("limit", String(params.limit));
  if (params?.offset) query.set("offset", String(params.offset));
  const qs = query.toString();
  return apiRequest(`${BASE}/payables${qs ? `?${qs}` : ""}`);
}

export function payPayableInvoice(
  invoiceId: string,
  payload: PayablePaymentCreate
): Promise<PayablePaymentPublic> {
  return apiRequest(`${BASE}/payables/${invoiceId}/payments`, { method: "POST", body: payload });
}

